import { useEffect, useRef } from 'react';
import { heartbeat } from 'packages/authentication_repository';
import {
  IDLE_TIMEOUT_MS,
  THROTTLE_DELAY_MS,
  HEARTBEAT_INTERVAL_MS,
  STORAGE_KEY,
  ACTIVITY_EVENTS,
} from './constants';

const useIdleTimeout = (onLogout: () => void, isAuthenticated: boolean) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastHeartbeatRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const sendHeartbeat = async () => {
      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;

      if (timeSinceLastHeartbeat < HEARTBEAT_INTERVAL_MS) {
        return;
      }

      try {
        await heartbeat();
        lastHeartbeatRef.current = Date.now();
      } catch (error) {
        // Silent fail - already handled by interceptor
      }
    };

    const resetTimer = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        onLogout();
      }, IDLE_TIMEOUT_MS);

      const now = Date.now();
      lastActivityRef.current = now;

      try {
        localStorage.setItem(STORAGE_KEY, now.toString());
      } catch (error) {
        // Silent fail
      }
    };

    let isThrottled = false;
    const handleActivity = () => {
      if (isThrottled) {
        return;
      }

      isThrottled = true;
      resetTimer();

      sendHeartbeat();

      setTimeout(() => {
        isThrottled = false;
      }, THROTTLE_DELAY_MS);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        resetTimer();
      }
    };

    resetTimer();

    ACTIVITY_EVENTS.forEach((eventType) => {
      window.addEventListener(eventType, handleActivity, { passive: true });
    });

    window.addEventListener('storage', handleStorageChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      ACTIVITY_EVENTS.forEach((eventType) => {
        window.removeEventListener(eventType, handleActivity);
      });

      window.removeEventListener('storage', handleStorageChange);
    };
  }, [onLogout, isAuthenticated]);
};

export default useIdleTimeout;
